import React, { FC, useEffect, useState } from 'react';
import './UserInventory.css';
import eFormType from '../../../main/assets/enums/eFormType';
import { useForm } from 'react-hook-form';
import GeneralInput from '../../../main/components/generalInput/GeneralInput';
import IUser from '../../../main/interfaces/IUser';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { toast } from 'react-toastify';
import axiosInstance from '../../../main/initializers/axios';
import IRole from '../../../main/interfaces/IRole';
import GeneralSelect from '../../../main/components/select/GeneralSelect';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import { useTranslation } from 'react-i18next';
import { pageRedirectOptions } from '../../../main/constants/pageRedirect';
import { useCan } from '../../../core/authorization/PermissionProvider';

interface Props {
  tempUser?: any;
  roles?: IRole[];
  modalType: eFormType;
  onSubmitData: (data: any) => void;
  createOrUpdate: boolean;
}

type ChangePasswordForm = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const DEFAULT_PASSWORD = 'Aa123456';

const UserInventory: FC<Props> = (props: Props) => {
  const { tempUser, roles, modalType, createOrUpdate } = props;

  const isEdit = modalType === eFormType.Edit;
  const canSave = useCan('platform.memberships', isEdit ? 'update' : 'create');

  const [savedUserId, setSavedUserId] = useState<number>(isEdit ? tempUser?.id : 0);
  const [isChangePwdOpen, setIsChangePwdOpen] = useState(false);
  const [avatarBase64, setAvatarBase64] = useState<string>(isEdit ? (tempUser?.avatar ?? '') : '');

  const { i18n } = useTranslation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IUser>({
    defaultValues: {
      id: isEdit ? tempUser?.id : 0,
      roleId: isEdit ? tempUser?.roleId : 0,
      email: isEdit ? tempUser?.email : '',
      canPrintFiscal: isEdit ? tempUser?.canPrintFiscal ?? false : false,
      isActive: isEdit ? tempUser?.isActive ?? false : false,
      operatorCode: isEdit ? tempUser?.operatorCode : '',
      pageRedirect: isEdit ? (tempUser?.pageRedirect ?? 2) : 2,
      userName: isEdit ? tempUser?.userName : '',
      firstName: isEdit ? tempUser?.firstName : '',
      lastName: isEdit ? tempUser?.lastName : '',
      // password removed from UI
    },
  });

  // Change password form
  const {
    control: pwdControl,
    handleSubmit: handlePwdSubmit,
    reset: resetPwd,
    formState: { errors: pwdErrors },
  } = useForm<ChangePasswordForm>({
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
  });

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(i18n.t('users.pleaseSelectAPphotoOnly'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(i18n.t('users.thePhotoSizeMustBe<2MB'));
    }

    const base64 = await fileToBase64(file);
    setAvatarBase64(base64);
  };

  useEffect(() => {
    if (isEdit) {
      setSavedUserId(tempUser?.id ?? 0);
      setAvatarBase64(tempUser?.avatar ?? '');

      reset({
        id: tempUser?.id ?? 0,
        roleId: tempUser?.roleId ?? 0,
        email: tempUser?.email ?? '',
        canPrintFiscal: tempUser?.canPrintFiscal ?? false,
        isActive: tempUser?.isActive ?? false,
        operatorCode: tempUser?.operatorCode ?? '',
        pageRedirect: tempUser?.pageRedirect ?? 2,
        userName: tempUser?.userName ?? '',
        firstName: tempUser?.firstName ?? '',
        lastName: tempUser?.lastName ?? '',
      });
    }
  }, [tempUser, isEdit]);

  const openPwdModal = () => {
    if (!savedUserId) {
      toast.error(i18n.t('users.savetheUserFirst'));
      return;
    }
    resetPwd({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setIsChangePwdOpen(true);
  };

  const closePwdModal = () => {
    setIsChangePwdOpen(false);
    resetPwd({ oldPassword: '', newPassword: '', confirmPassword: '' });
  };

  const onSubmit = (data: any) => {
    if (!canSave) return;
    const params = {
      Id: data.id,
      RoleId: data.roleId,
      Email: data.email,
      CanPrintFiscal: data.canPrintFiscal,
      IsActive: data.isActive,
      OperatorCode: data.operatorCode,
      PageRedirect: Number(data.pageRedirect ?? 2),
      UserName: data.userName,
      FirstName: data.firstName,
      LastName: data.lastName,
      Avatar: avatarBase64,
    };

    axiosInstance
      .post(ApiConstants.User.update, params)
      .then((response) => {
        if (response.status === 200) {
          toast.success(isEdit ? i18n.t('users.updatedSuccessfully') : i18n.t('users.createdSuccessfully'));
          const returnedId = response.data?.id ?? response.data?.data?.id ?? response.data?.userId ?? data.id ?? 0;

          setSavedUserId(returnedId);

          if (!params.Id) {
            setIsChangePwdOpen(true);
            resetPwd({ oldPassword: DEFAULT_PASSWORD, newPassword: '', confirmPassword: '' });
          } else {
            props.onSubmitData(true);
          }
        } else {
          toast.error(i18n.t('orderDetails.somethingWentWrong'));
        }
      })
      .catch(() => toast.error(i18n.t('orderDetails.somethingWentWrong')));
  };

  useEffect(() => {
    if (createOrUpdate) {
      handleSubmit(onSubmit)();
    }
  }, [createOrUpdate]);

  const validatePwd = (v: ChangePasswordForm) => {
    const oldP = (v.oldPassword || '').trim();
    const newP = (v.newPassword || '').trim();
    const confP = (v.confirmPassword || '').trim();

    if (!oldP || !newP || !confP) {
      toast.error(i18n.t('users.pleaseFillInAllFields'));
      return false;
    }
    if (newP.length < 8) {
      toast.error(i18n.t('users.theNewPasswordMustBeatLeast8CharactersLong'));
      return false;
    }
    if (newP !== confP) {
      toast.error(i18n.t('users.theNewPasswordAndConfirmationDoNotMatch'));
    }
    if (newP === oldP) {
      toast.error(i18n.t('users.theNewPasswordMustBeDifferentFromTheOldOne'));
      return false;
    }
    return true;
  };

  const onChangePassword = (form: ChangePasswordForm) => {
    if (!savedUserId) {
      toast.error(i18n.t('users.userIdIsMissing'));
      return;
    }
    if (!validatePwd(form)) return;

    const payload = {
      userId: savedUserId,
      oldPassword: form.oldPassword,
      newPassword: form.newPassword,
      confirmPassword: form.confirmPassword,
    };

    axiosInstance
      .post(ApiConstants.User.changePassword, payload)
      .then((res) => {
        if (res.status === 200) {
          toast.success(i18n.t('users.passwordChangedSuccessfully'));
          closePwdModal();
        } else {
          toast.error(i18n.t('orderDetails.somethingWentWrong'));
        }
      })
      .catch(() => toast.error(i18n.t('orderDetails.somethingWentWrong')));
  };

  return (
    <div className="platformInventoryForm">
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div className="platformInventoryFields">
          <div className="platformInventoryRow">
            <div className="platformInventoryField">
              <GeneralInput
                required={false}
                label={i18n.t('entity.UserName')}
                name="userName"
                type="text"
                placeholder={i18n.t('entity.UserName')}
                errors={errors}
                control={control}
              />
            </div>

            {/* Password removed */}
            <div className="platformInventoryField" style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={openPwdModal}
                disabled={!savedUserId}
                title={!savedUserId ? i18n.t('users.saveTheUserFirst') : i18n.t('generic.changePassword')}
              >
                {i18n.t('generic.changePassword')}
              </button>
            </div>
          </div>

          <div className="platformInventoryRow">
            <div className="platformInventoryField">
              <GeneralInput
                required={false}
                label={i18n.t('entity.FirstName')}
                name="firstName"
                type="text"
                placeholder={i18n.t('entity.FirstName')}
                errors={errors}
                control={control}
              />
            </div>
            <div className="platformInventoryField">
              <GeneralInput
                required={false}
                label={i18n.t('entity.LastName')}
                name="lastName"
                type="text"
                placeholder={i18n.t('entity.LastName')}
                errors={errors}
                control={control}
              />
            </div>
          </div>

          <div className="platformInventoryRow">
            <div className="platformInventoryField">
              <GeneralInput
                required={false}
                label={i18n.t('generic.email')}
                name="email"
                type="text"
                placeholder={i18n.t('generic.email')}
                errors={errors}
                control={control}
              />
            </div>
            <div className="platformInventoryField">
              <GeneralSelect
                preSelectedVal={i18n.t('users.selectRole')}
                setSelectedVal={(val) => console.log(val)}
                data={roles}
                required={false}
                control={control}
                errors={errors}
                label={i18n.t('generic.role')}
                name="roleId"
                value="name"
              />
            </div>
          </div>

          <div className="platformInventoryRow">
            <div className="platformInventoryField">
              <GeneralSelect
                preSelectedVal="Select page"
                setSelectedVal={() => undefined}
                data={pageRedirectOptions}
                required={false}
                control={control}
                errors={errors}
                label="Page Redirect"
                name="pageRedirect"
                value="name"
              />
            </div>
            <div className="platformInventoryField">
              <GeneralInput
                required={false}
                label="Active"
                name="isActive"
                type="checkbox"
                placeholder="isActive"
                errors={errors}
                control={control}
              />
            </div>
          </div>

          <div className="platformInventoryField">
            <GeneralInput
              required={false}
              label={i18n.t('users.canPrintFiscal', { defaultValue: 'Can print fiscal' })}
              name="canPrintFiscal"
              type="checkbox"
              placeholder="canPrintFiscal"
              errors={errors}
              control={control}
            />
          </div>

          <div className="platformInventoryField">
            <GeneralInput
              required={false}
              label={i18n.t('users.operatorCode', { defaultValue: 'Operator Code' })}
              name="operatorCode"
              type="text"
              placeholder={i18n.t('users.operatorCode', { defaultValue: 'Operator Code' })}
              errors={errors}
              control={control}
            />
          </div>

          {/* ✅ Avatar */}
          <div className="platformInventoryField">
            <label>{i18n.t('users.avatar')}</label>
            <input type="file" accept="image/*" onChange={onAvatarChange} />
            {avatarBase64 && (
              <img src={avatarBase64} alt="avatar preview" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', marginTop: 8 }} />
            )}
          </div>
        </div>
      </form>

      {isChangePwdOpen && (
        <GeneralModal
          headerName={i18n.t('generic.changePassword')}
          handleClose={closePwdModal}
          handleClick={handlePwdSubmit(onChangePassword)}
          width="520px"
          radius="14px"
        >
          <GeneralInput
            required={true}
            label={i18n.t('generic.currentPassword')}
            name="oldPassword"
            type="password"
            placeholder={i18n.t('generic.currentPassword')}
            errors={pwdErrors}
            control={pwdControl}
            autoComplete="new-password"
            htmlName="old_pwd_no_autofill"
          />

          <GeneralInput
            required={true}
            label={i18n.t('generic.newPassword')}
            name="newPassword"
            type="password"
            placeholder={i18n.t('generic.newPassword')}
            errors={pwdErrors}
            control={pwdControl}
            autoComplete="new-password"
            htmlName="new_pwd_no_autofill"
          />

          <GeneralInput
            required={true}
            label={i18n.t('generic.confirmPassword')}
            name="confirmPassword"
            type="password"
            placeholder={i18n.t('generic.confirmPassword')}
            errors={pwdErrors}
            control={pwdControl}
            autoComplete="new-password"
            htmlName="confirm_pwd_no_autofill"
          />

          <small style={{ color: 'var(--muted)' }}>{i18n.t('users.theNewPasswordMustBeatLeast8CharactersLong')}</small>
        </GeneralModal>
      )}
    </div>
  );
};

export default UserInventory;
